/**
 * Textos dos componentes compartilhados (SPEC 10.3: nenhuma string solta em JSX).
 * pt-BR, caixa de frase, imperativo nas ações, sem exclamação na interface (SPEC 9.11).
 *
 * Este arquivo vai no bundle inicial (10.4): aqui só o que a moldura e a primeira dobra usam, mais
 * o que as telas importam direto. Textos de decisão, evidência, atividade e integração ficam em
 * `domain/copy.ts`, que desce com quem os usa.
 */
import type { DataSource, IntegrationSource, IntegrationState, KpiStatus, PaceState } from '@rota/contracts';

export const copy = {
  common: {
    close: 'Fechar',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    retry: 'Tentar de novo',
    more: 'Mais opções',
    loading: 'Carregando',
    noData: 'sem dado',
    dash: '—',
    info: 'Sobre esta métrica',
    clear: 'Limpar',
    search: 'Buscar',
    remove: (label: string) => `Remover ${label}`,
    increment: 'Aumentar',
    decrement: 'Diminuir',
    sortAsc: 'ordem crescente',
    sortDesc: 'ordem decrescente',
    selectRow: 'Selecionar linha',
    selectAll: 'Selecionar todas as linhas',
    total: 'Total',
    swipeHint: 'Ações da linha',
    sheetHandle: 'Arrastar para redimensionar',
    progress: (n: number, min: number, unit: string) => `${n} de ${min} ${unit}`,
  },

  delta: {
    up: 'alta de',
    down: 'queda de',
    flat: 'sem variação',
    better: 'melhor',
    worse: 'pior',
    none: 'sem comparação',
  },

  status: {
    kpi: {
      good: 'Bom',
      warn: 'Atenção',
      bad: 'Crítico',
      neutral: 'Neutro',
      insufficient: 'Dados insuficientes',
    } satisfies Record<KpiStatus, string>,
    integration: {
      connected: 'Conectada',
      degraded: 'Instável',
      error: 'Com erro',
      disconnected: 'Desconectada',
      not_configured: 'Não configurada',
    } satisfies Record<IntegrationState, string>,
    pace: {
      ahead: 'à frente',
      on_track: 'no ritmo',
      behind: 'atrás',
    } satisfies Record<PaceState, string>,
  },

  sources: {
    meta: 'Meta',
    vturb: 'VTurb',
    kiwify: 'Kiwify',
    tracker: 'Tracker',
    rules: 'Regras',
    system: 'Sistema',
  } satisfies Record<DataSource, string>,

  integrationLong: {
    meta: 'Meta Ads',
    vturb: 'VTurb',
    kiwify: 'Kiwify',
    tracker: 'Tracker próprio',
  } satisfies Record<IntegrationSource, string>,

  freshness: {
    tag: (source: string, relative: string) => `${source} · ${relative}`,
    aria: (source: string, relative: string, stale: boolean) =>
      `Dados de ${source} atualizados ${relative}${stale ? ', atrasados' : ''}`,
    live: 'ao vivo',
  },

  kpiCard: {
    vs: (label: string) => `vs ${label}`,
    insufficient: (n: string, min: string, unit: string) => `Dados insuficientes · ${n} de ${min} ${unit}`,
    /** Amostra do cartão: a mesma frase do sheet de métrica ("14 compradores (mínimo 30)"). */
    sample: (n: string, min: string, unit: string) => `Amostra pequena: ${n} ${unit} (mínimo ${min})`,
    /** Sufixo só para leitor de tela: o nome do botão começa pelo valor visível (WCAG 2.5.3). */
    openDetail: (label: string) => `, abrir detalhes de ${label}`,
    unavailable: 'n/d',
    /** Nome acessível do ponto de status ao lado do título. */
    status: (label: string) => `Status: ${label}`,
  },

  widget: {
    errorTitle: 'Não foi possível carregar',
    errorCode: (code: string) => `Código ${code}`,
    renderError: 'render',
    insufficientTitle: 'Dados insuficientes',
    showTable: 'Ver como tabela',
    hideTable: 'Ocultar tabela',
    actions: 'Ações do widget',
    refreshing: 'Atualizando',
    stale: 'Desatualizado',
    gateDefault: (source: string) => `Conecte ${source} para ver este quadro.`,
    sourceArticle: { meta: 'a Meta', vturb: 'a VTurb', kiwify: 'a Kiwify', tracker: 'o tracker' },
  },

  gate: {
    connect: 'Conectar',
    /** Concordância por fonte: a Meta, a VTurb, a Kiwify; o tracker. */
    title: {
      meta: 'Meta Ads não conectada',
      vturb: 'VTurb não conectada',
      kiwify: 'Kiwify não conectada',
      tracker: 'Tracker próprio não conectado',
    } satisfies Record<IntegrationSource, string>,
  },

  emptyDefault: {
    title: 'Nada por aqui ainda',
  },

  table: {
    empty: 'Nenhuma linha',
  },

  spinner: 'Carregando',

  /** Ações da decisão usadas também pelas telas (DecisionSheet); o resto fica em `domain/copy.ts`. */
  decision: {
    apply: 'Aplicar',
    markDone: 'Marcar como feito',
    snooze: 'Adiar',
    dismiss: 'Ignorar',
    doneConfirm: 'Marcar como feito',
    snoozeOptions: {
      m30: '30 min',
      h1: '1 h',
      checkpoint: (time: string) => `Até o próximo checkpoint (${time})`,
      tomorrow: 'Amanhã 09:00',
    },
    dismissReasons: {
      resolved_already: 'Já resolvido',
      false_alarm: 'Falso alarme',
      strategic: 'Decisão estratégica',
      other: 'Outro',
    },
    sending: (sec: number) => `Enviando em ${sec} s`,
    undo: 'Desfazer',
  },
} as const;

export type Copy = typeof copy;
